import React, { useState } from "react";
import { Eye, Edit, FileText } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@shared/ui/badge";
import { useUpdateGlCodeStatus, useExportGlCodes } from "../../hooks";
import { DetailsModal } from "../details/DetailsModal";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";

const TruncatedList = ({
  items,
  renderItem,
  maxItems = 3,
}: {
  items: any[];
  renderItem: (item: any, i: number, isLastVisible: boolean) => React.ReactNode;
  maxItems?: number;
}) => {
  if (!items || items.length === 0) return null;

  const visibleItems = items.slice(0, maxItems);
  const hiddenItems = items.slice(maxItems);

  return (
    <div className="flex gap-1 items-center">
      {visibleItems.map((item, i) =>
        renderItem(
          item,
          i,
          i === visibleItems.length - 1 && hiddenItems.length === 0,
        ),
      )}
      {hiddenItems.length > 0 && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-pointer">
                <Badge
                  variant="outline"
                  className="bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                >
                  +{hiddenItems.length}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-1 max-w-xs">
                {hiddenItems.map((item, i) => (
                  <span key={i} className="text-sm break-words">
                    {item.label || item}
                  </span>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

interface GlCodeListProps {
  data: any[];
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  filterPanel?: React.ReactNode;
  onSearch?: (searchTerm: string) => void;
}

const GlCodeList: React.FC<GlCodeListProps> = ({
  data,
  totalItems,
  currentPage,
  itemsPerPage,
  onPageChange,
  isLoading,
  filterPanel,
  onSearch,
}) => {
  const navigate = useNavigate();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [searchParams] = useSearchParams();

  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useUpdateGlCodeStatus();
  const { mutate: exportCsv } = useExportGlCodes();

  const handleExport = () => {
    const params: Record<string, any> = {};
    if (searchParams.get("status")) params.status = searchParams.get("status");
    if (searchParams.get("gl_type"))
      params.gl_type = searchParams.get("gl_type");
    if (searchParams.get("search")) params.search = searchParams.get("search");
    exportCsv(params);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [glCodeToToggle, setGlCodeToToggle] = useState<any>(null);

  const handleToggleClick = (row: any) => {
    setGlCodeToToggle(row);
    setIsConfirmModalOpen(true);
  };

  // optimistically track status
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, boolean>
  >({});

  const handleConfirmToggle = () => {
    if (glCodeToToggle) {
      const id = glCodeToToggle.glCode?.toString();
      const originalStatus = glCodeToToggle.status;
      const isCurrentlyActive =
        statusOverrides[id] ??
        (originalStatus === 1 ||
          originalStatus === true ||
          originalStatus === "1");
      const newStatus = isCurrentlyActive ? 2 : 1;

      setStatusOverrides((prev) => ({ ...prev, [id]: !isCurrentlyActive }));
      updateStatus(
        { id, status: newStatus },
        {
          onSuccess: () => {
            setIsConfirmModalOpen(false);
            setGlCodeToToggle(null);
          },
          onError: () => {
            setStatusOverrides((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
            setIsConfirmModalOpen(false);
            setGlCodeToToggle(null);
          },
        },
      );
    }
  };

  const columns: TableColumn<any>[] = [
    {
      key: "glCode",
      header: "GL Code",
      type: "custom",
      render: (value: any, row: any) => (
        <span className="font-semibold text-gray-900">
          {row.glCode || "N/A"}
        </span>
      ),
    },
    {
      key: "glName",
      header: "GL Name",
      type: "custom",
      render: (value: any, row: any) => (
        <span className="text-gray-600">{row.glName || "N/A"}</span>
      ),
    },
    {
      key: "glType",
      header: "GL Type",
      type: "custom",
      render: (value: any, row: any) => {
        const types = row.glTypes || [];
        if (!types.length) return null;

        return (
          <TruncatedList
            items={types}
            renderItem={(type: any, i: number) => (
              <Badge
                key={i}
                variant="outline"
                className="bg-blue-50 text-blue-600 border-blue-200 whitespace-nowrap"
              >
                {type.label || type}
              </Badge>
            )}
          />
        );
      },
    },
    {
      key: "source",
      header: "Source",
      type: "custom",
      render: (value: any, row: any) => {
        const sources = row.sources || [];
        if (!sources.length) return null;

        return (
          <TruncatedList
            items={sources}
            renderItem={(src: any, i: number) => {
              const sourceLabel = src.label || src;
              let colorClass = "bg-gray-50 text-gray-600 border-gray-200";
              if (sourceLabel === "Billing")
                colorClass = "bg-blue-50 text-blue-600 border-blue-200";
              else if (sourceLabel === "Services")
                colorClass = "bg-green-50 text-green-600 border-green-200";
              else if (sourceLabel === "Service Orders")
                colorClass = "bg-purple-50 text-purple-600 border-purple-200";

              return (
                <Badge
                  key={i}
                  variant="outline"
                  className={`${colorClass} whitespace-nowrap`}
                >
                  {sourceLabel}
                </Badge>
              );
            }}
          />
        );
      },
    },
    {
      key: "transactionCategory",
      header: "Transaction Category",
      type: "custom",
      render: (value: any, row: any) => {
        const cats = row.transactionCategories || [];
        if (!cats.length) return <span className="text-gray-600">N/A</span>;

        return (
          <TruncatedList
            items={cats}
            renderItem={(cat: any, i: number) => (
              <Badge
                key={i}
                variant="outline"
                className="bg-cyan-50 text-cyan-600 border-cyan-200 whitespace-nowrap"
              >
                {cat.label || cat}
              </Badge>
            )}
          />
        );
      },
    },
    {
      key: "transactionType",
      header: "Transaction Type",
      type: "custom",
      render: (value: any, row: any) => {
        const types = row.transactionTypes || [];
        if (!types.length) return <span className="text-gray-600">N/A</span>;

        return (
          <TruncatedList
            items={types}
            renderItem={(type: any, i: number) => (
              <Badge
                key={i}
                variant="outline"
                className="bg-amber-50 text-amber-600 border-amber-200 whitespace-nowrap"
              >
                {type.label || type}
              </Badge>
            )}
          />
        );
      },
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (value: any, row: any) => {
        const id = row.glCode?.toString();
        const originalStatus = row.status;
        const isActive =
          statusOverrides[id] ??
          (originalStatus === 1 ||
            originalStatus === true ||
            originalStatus === "1");
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
  ];

  const actions: TableAction<any>[] = [
    {
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: (row) => {
        setSelectedRowData({ glCode: row.glCode });
        setShowDetailsModal(true);
      },
    },
    {
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: (row) => navigate(`/bx/gl-code/edit/${row.glCode}`),
    },
    {
      label: "Make Inactive",
      hidden: (row) => {
        const id = row.glCode?.toString();
        const originalStatus = row.status;
        const isActive =
          statusOverrides[id] ??
          (originalStatus === 1 ||
            originalStatus === true ||
            originalStatus === "1");
        return !isActive;
      },
      onClick: (row) => handleToggleClick(row),
    },
    {
      label: "Make Active",
      hidden: (row) => {
        const id = row.glCode?.toString();
        const originalStatus = row.status;
        const isActive =
          statusOverrides[id] ??
          (originalStatus === 1 ||
            originalStatus === true ||
            originalStatus === "1");
        return isActive;
      },
      onClick: (row) => handleToggleClick(row),
    },
  ];

  return (
    <>
      <GenericTable
        data={data}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by GL Code, GL Name..."
        onSearch={onSearch || (() => {})}
        filterPanel={filterPanel}
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil(totalItems / itemsPerPage) || 1,
          totalItems: totalItems,
          itemsPerPage,
          startItem: totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0,
          endItem: Math.min(currentPage * itemsPerPage, totalItems),
        }}
        onPageChange={onPageChange}
        onExport={handleExport}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No GL Codes found"
        emptyIcon={<FileText className="h-8 w-8 text-muted-foreground mb-2" />}
      />

      {showDetailsModal && (
        <DetailsModal
          open={showDetailsModal}
          onOpenChange={setShowDetailsModal}
          data={selectedRowData}
        />
      )}

      <DeleteConfirmationDialog
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmToggle}
        title={
          (statusOverrides[glCodeToToggle?.id?.toString()] ??
          (glCodeToToggle?.status === 1 ||
            glCodeToToggle?.status === true ||
            glCodeToToggle?.status === "1"))
            ? "Deactivate GL Code"
            : "Activate GL Code"
        }
        description={
          (statusOverrides[glCodeToToggle?.id?.toString()] ??
          (glCodeToToggle?.status === 1 ||
            glCodeToToggle?.status === true ||
            glCodeToToggle?.status === "1"))
            ? `Are you sure you want to make "${glCodeToToggle?.glName || "N/A"}" inactive?`
            : `Are you sure you want to make "${glCodeToToggle?.glName || "N/A"}" active?`
        }
        confirmText={
          (statusOverrides[glCodeToToggle?.id?.toString()] ??
          (glCodeToToggle?.status === 1 ||
            glCodeToToggle?.status === true ||
            glCodeToToggle?.status === "1"))
            ? "Deactivate"
            : "Activate"
        }
        isLoading={isUpdatingStatus}
      />
    </>
  );
};

export default GlCodeList;
